/**
 * The map camera.
 *
 * Deliberately free of any DOM or canvas dependency: it is pure arithmetic over a
 * viewport rectangle, which makes the fiddly parts — clamping at the poles, wrapping
 * at the date line, converting a mouse position to a tile — unit-testable rather than
 * something you verify by dragging the map around and squinting.
 *
 * Two coordinate spaces:
 *   WORLD  pixels, the whole map laid out at the current zoom. Wraps east-west.
 *   SCREEN pixels, relative to the canvas's top-left corner.
 */

import { hexToPixel, offsetToAxial, pixelToHex, type Hex } from '../core/hex/index.js';
import type { TileGrid } from '../core/state/types.js';

/** Hex circumradius in pixels at each end of the zoom range. */
export const MIN_TILE_SIZE = 6;
export const MAX_TILE_SIZE = 64;
export const DEFAULT_TILE_SIZE = 22;

export interface Camera {
  /** Centre of the view, in world pixels. */
  x: number;
  y: number;
  /** Hex circumradius in pixels. The single source of zoom. */
  tileSize: number;
  viewportWidth: number;
  viewportHeight: number;
}

export interface WorldMetrics {
  /** Horizontal distance between adjacent hex centres. Also the wrap period. */
  columnStep: number;
  /** Vertical distance between hex rows. */
  rowStep: number;
  width: number;
  height: number;
}

export function worldMetrics(map: TileGrid, tileSize: number): WorldMetrics {
  const columnStep = Math.sqrt(3) * tileSize;
  const rowStep = 1.5 * tileSize;
  return {
    columnStep,
    rowStep,
    width: columnStep * map.width,
    height: rowStep * (map.height - 1) + tileSize * 2,
  };
}

/**
 * The smallest tile size that still keeps the world at least as wide as the view.
 *
 * A wrapping map is a cylinder with no edges, so nothing stops the viewport spanning
 * more than one full lap — and when it does, the player sees two copies of their world
 * side by side. On an 80-wide map the world is 3049px across at the default zoom but
 * only 831px at the old fixed minimum, so any ordinary window showed the map twice
 * over as soon as you zoomed out.
 *
 * Deriving the floor from the map and the viewport means one lap exactly fills the
 * window at maximum zoom-out: the whole world is still visible, and a second copy is
 * impossible. Maps that do not wrap keep the fixed floor, because they cannot repeat.
 */
export function minTileSize(map: TileGrid, viewportWidth: number): number {
  if (!map.wrapX) return MIN_TILE_SIZE;
  return Math.max(MIN_TILE_SIZE, viewportWidth / (Math.sqrt(3) * map.width));
}

export function createCamera(map: TileGrid, viewportWidth: number, viewportHeight: number): Camera {
  const metrics = worldMetrics(map, DEFAULT_TILE_SIZE);
  return {
    x: metrics.width / 2,
    y: metrics.height / 2,
    tileSize: DEFAULT_TILE_SIZE,
    viewportWidth,
    viewportHeight,
  };
}

/**
 * Keep the camera somewhere sensible.
 *
 * East-west wraps, so x is taken modulo the world width and never clamped — you can
 * circumnavigate. North-south does not wrap, so y is clamped to keep the poles at
 * the edge of the view rather than letting the map drift off into empty space. When
 * the world is shorter than the viewport it is centred instead of clamped, which
 * matters at low zoom on small maps.
 */
export function clampCamera(camera: Camera, map: TileGrid): void {
  // Enforced here rather than only in zoomTo, because the viewport can grow without
  // anyone touching the zoom — resizing the window, or hiding the side panel.
  const floor = minTileSize(map, camera.viewportWidth);
  if (camera.tileSize < floor) camera.tileSize = Math.min(floor, MAX_TILE_SIZE);

  const metrics = worldMetrics(map, camera.tileSize);

  camera.x = ((camera.x % metrics.width) + metrics.width) % metrics.width;

  if (metrics.height <= camera.viewportHeight) {
    camera.y = metrics.height / 2;
  } else {
    const margin = camera.viewportHeight / 2;
    camera.y = Math.min(metrics.height - margin, Math.max(margin, camera.y));
  }
}

export function panBy(camera: Camera, map: TileGrid, dx: number, dy: number): void {
  camera.x += dx;
  camera.y += dy;
  clampCamera(camera, map);
}

/**
 * Zoom, keeping the world point under (anchorX, anchorY) in screen space fixed.
 *
 * Without the anchor, zooming with the wheel walks the map out from under the
 * pointer, which feels broken in a way people notice immediately even if they cannot
 * name it.
 */
export function zoomTo(
  camera: Camera,
  map: TileGrid,
  nextTileSize: number,
  anchorX: number,
  anchorY: number,
): void {
  const clamped = Math.min(MAX_TILE_SIZE, Math.max(minTileSize(map, camera.viewportWidth), nextTileSize));
  if (clamped === camera.tileSize) return;

  const before = screenToWorld(camera, anchorX, anchorY);
  const ratio = clamped / camera.tileSize;
  camera.tileSize = clamped;
  // World coordinates scale with tile size, so the same world point is now elsewhere.
  camera.x = before.x * ratio - (anchorX - camera.viewportWidth / 2);
  camera.y = before.y * ratio - (anchorY - camera.viewportHeight / 2);
  clampCamera(camera, map);
}

export function screenToWorld(camera: Camera, screenX: number, screenY: number): { x: number; y: number } {
  return {
    x: camera.x + (screenX - camera.viewportWidth / 2),
    y: camera.y + (screenY - camera.viewportHeight / 2),
  };
}

export function worldToScreen(camera: Camera, worldX: number, worldY: number): { x: number; y: number } {
  return {
    x: worldX - camera.x + camera.viewportWidth / 2,
    y: worldY - camera.y + camera.viewportHeight / 2,
  };
}

/** World-pixel position of a tile's centre. */
export function tileCentre(col: number, row: number, tileSize: number): { x: number; y: number } {
  const point = hexToPixel(offsetToAxial(col, row), tileSize);
  // hexToPixel puts column 0 at x = 0 for even rows; shift so the map starts at the origin.
  return { x: point.x + (Math.sqrt(3) * tileSize) / 2, y: point.y + tileSize };
}

/**
 * The tile under a screen position, or null if the point is off the top or bottom of
 * the world. Columns wrap, so there is no horizontal miss.
 */
export function tileAtScreen(
  camera: Camera,
  map: TileGrid,
  screenX: number,
  screenY: number,
): { col: number; row: number } | null {
  const world = screenToWorld(camera, screenX, screenY);
  const hex: Hex = pixelToHex(
    world.x - (Math.sqrt(3) * camera.tileSize) / 2,
    world.y - camera.tileSize,
    camera.tileSize,
  );
  const row = hex.r;
  if (row < 0 || row >= map.height) return null;
  const col = hex.q + (row - (row & 1)) / 2;
  return { col: ((col % map.width) + map.width) % map.width, row };
}

export interface VisibleRange {
  minCol: number;
  maxCol: number;
  minRow: number;
  maxRow: number;
}

/**
 * The block of tiles that could touch the viewport, with a one-tile margin.
 *
 * Column bounds are NOT wrapped into [0, width): the renderer walks the raw range and
 * wraps each index as it draws, which is what makes the seam invisible when the view
 * straddles it. Wrapping here instead would give minCol > maxCol and the loop would
 * need a special case.
 */
export function visibleRange(camera: Camera, map: TileGrid): VisibleRange {
  const metrics = worldMetrics(map, camera.tileSize);
  const left = camera.x - camera.viewportWidth / 2;
  const top = camera.y - camera.viewportHeight / 2;

  return {
    minCol: Math.floor(left / metrics.columnStep) - 1,
    maxCol: Math.ceil((left + camera.viewportWidth) / metrics.columnStep) + 1,
    minRow: Math.max(0, Math.floor(top / metrics.rowStep) - 1),
    maxRow: Math.min(map.height - 1, Math.ceil((top + camera.viewportHeight) / metrics.rowStep) + 1),
  };
}

/** Move the camera so a given tile is centred. Used by "go to" and by the minimap later. */
export function centreOn(camera: Camera, map: TileGrid, col: number, row: number): void {
  const centre = tileCentre(col, row, camera.tileSize);
  camera.x = centre.x;
  camera.y = centre.y;
  clampCamera(camera, map);
}
